/**
 * "How was this before the tab?" — on request, and silent otherwise.
 *
 * Collapsed by default and deliberately quiet: the dashboard's job is to report the current range,
 * and a permanent before/after banner turns every visit into an argument about a project. Someone
 * who wants the comparison asks for it; everyone else sees one muted line.
 *
 * It is also the one number here that cannot be recomputed — see `responseBaseline.ts`.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatHours, formatPercent } from './chartTokens';
import { MIN_OBSERVATIONS_TO_COMPARE, RESPONSE_BASELINE, compareDuration } from './responseBaseline';

function CompareRow({
  label,
  baselineHours,
  liveHours,
  comparable,
}: {
  label: string;
  baselineHours: number;
  liveHours: number | null;
  /** False when the live sample is too small for a ratio to mean anything. */
  comparable: boolean;
}) {
  const delta = comparable ? compareDuration(baselineHours, liveHours) : null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs text-zinc-600">{label}</span>
      <span className="flex items-baseline gap-2 text-xs">
        <span className="text-zinc-400">{formatHours(baselineHours)}</span>
        <span className="text-zinc-300">→</span>
        <span className="font-medium tabular-nums text-zinc-900">{formatHours(liveHours)}</span>
        {delta && (
          // Neither green nor red: this panel reports a measurement, and colouring the direction
          // makes the dashboard take a side on work the reader is trying to judge.
          <span className="text-zinc-500">
            {delta.factor}× {delta.faster ? 'faster' : 'slower'}
          </span>
        )}
      </span>
    </div>
  );
}

export function BaselineCompare({
  medianReplyHours,
  p90ReplyHours,
  repliesObserved,
}: {
  medianReplyHours: number | null;
  p90ReplyHours: number | null;
  /** How many real sends the live figures rest on. Decides whether a ratio is shown at all. */
  repliesObserved: number;
}) {
  const [open, setOpen] = useState(false);
  const comparable = repliesObserved >= MIN_OBSERVATIONS_TO_COMPARE;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs text-zinc-400 transition-colors hover:text-zinc-600"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {open ? 'Hide the before/after' : 'Compare with before this tab'}
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4">
          <div className="divide-y divide-zinc-200">
            <CompareRow
              label="Median time to reply"
              baselineHours={RESPONSE_BASELINE.medianReplyHours}
              liveHours={medianReplyHours}
              comparable={comparable}
            />
            <CompareRow
              label="Slowest 10% to reply"
              baselineHours={RESPONSE_BASELINE.p90ReplyHours}
              liveHours={p90ReplyHours}
              comparable={comparable}
            />
          </div>

          {/*
            Said plainly, in the place where someone would otherwise read a ratio off five sends and
            put it in a slide. The figures still show; only the comparison is withheld.
          */}
          {!comparable && (
            <p className="mt-3 text-xs text-zinc-500">
              Only {repliesObserved} send{repliesObserved === 1 ? '' : 's'} measured in this range —
              too few to compare against {RESPONSE_BASELINE.pairs} pairs, so no ratio is shown. Widen
              the range.
            </p>
          )}

          {/*
            Baseline figures with no live counterpart YET. Shown rather than dropped because they are
            the most useful two numbers in the set: the working-hours median is the honest read of
            how fast the team was, and the unanswered share is what this tab exists to move.
          */}
          <p className="mt-3 border-t border-zinc-200 pt-3 text-xs text-zinc-500">
            Also measured back then, with nothing live to compare against yet:{' '}
            <strong className="font-medium text-zinc-700">
              {formatHours(RESPONSE_BASELINE.medianBusinessHours)}
            </strong>{' '}
            median counting only working hours,{' '}
            <strong className="font-medium text-zinc-700">
              {formatPercent(RESPONSE_BASELINE.withinDayRate)}
            </strong>{' '}
            answered within a day, and{' '}
            <strong className="font-medium text-zinc-700">
              {formatPercent(RESPONSE_BASELINE.unansweredRate)}
            </strong>{' '}
            of threads carrying a real candidate question never answered at all.
          </p>

          {/*
            The comparison's own boundary, next to the numbers rather than in a doc. The two samples
            are built differently and someone will otherwise quote the ratio as a clean result.
          */}
          <p className="mt-2 text-xs text-zinc-400">
            {RESPONSE_BASELINE.pairs} question→reply pairs from {RESPONSE_BASELINE.mailboxes}{' '}
            recruiter mailboxes, {RESPONSE_BASELINE.period}, timestamped from Gmail at both ends.
            Indicative rather than exact: the baseline counted every reply those mailboxes sent,
            while the live figure sees only threads still in the queue when the reply landed.
          </p>
        </div>
      )}
    </div>
  );
}
